import { Chip } from "components/DesignSystem/Chips/Chips";
import { Filter } from "components/DesignSystem/Filter/Filter";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useGetUserProfileQuery } from "store/apis/profile";
import {
  FILTER_BOOKS_PENDING,
  FILTER_ETD_DUE_TODAY,
  FILTER_ETD_OVERDUE,
  FILTER_ETD_UNSET,
  FILTER_GLOBAL_STATUS_COMPLETED,
  FILTER_IN_PROGRESS,
  FILTER_INFORMATION_REQUIRED,
  FILTER_IS_AMENDMENT,
  FILTER_IS_URGENT,
  FILTER_MANDATORY_INFORMATION_MISSING,
  FILTER_INTERNAL_REVIEW,
  FILTER_READY_TO_PICK,
  FILTER_SERVICE_TEAM_MEMBER,
  FilterKeys,
  FilterValues,
  STATUS_DRAFT_SHARED,
  STATUS_ON_HOLD,
  STATUS_READY_TO_FILE,
  STATUS_UNPAID,
} from "./Task1120FilterOptions";

export type TrackerFilterOptions = FilterValues;

export type Task1120TrackerFilterType = {
  updateFilter: <S extends FilterKeys>(
    name: S,
    newValue: FilterValues[S]
  ) => void;
  values: FilterValues;
};

export const Task1120TrackerFilter = ({
  updateFilter,
  values,
}: Task1120TrackerFilterType) => {
  const { isCpa, isAdmin, isForeignCA } = useRoleBasedView();
  const { data: profile } = useGetUserProfileQuery();
  const profileUuid = profile?.uuid;

  const isCpaOnly = isCpa && !isAdmin && !isForeignCA;

  const clearCpaPresetFilters = () => {
    updateFilter(FILTER_READY_TO_PICK, null);
    updateFilter(FILTER_IS_URGENT, null);
    updateFilter(FILTER_SERVICE_TEAM_MEMBER, null);
  };

  if (isCpaOnly) {
    return (
      <div className="t-flex t-gap-2">
        <Chip
          isActive={Boolean(values.READY_TO_BE_PICKED_UP)}
          filterType={FILTER_READY_TO_PICK}
          onClick={() => {
            clearCpaPresetFilters();
            updateFilter(FILTER_READY_TO_PICK, true);
          }}
          onRemove={clearCpaPresetFilters}
          isFixedFilter
        >
          Ready to be picked up
        </Chip>
        <Chip
          isActive={Boolean(values.IS_URGENT)}
          filterType={FILTER_IS_URGENT}
          onClick={() => {
            clearCpaPresetFilters();
            updateFilter(FILTER_IS_URGENT, true);
          }}
          onRemove={clearCpaPresetFilters}
          isFixedFilter
        >
          Urgent
        </Chip>
        <Chip
          isActive={Boolean(
            profileUuid && values.SERVICE_TEAM_MEMBER === profileUuid
          )}
          filterType={FILTER_SERVICE_TEAM_MEMBER}
          onClick={() => {
            if (profileUuid) {
              clearCpaPresetFilters();
              updateFilter(FILTER_SERVICE_TEAM_MEMBER, profileUuid);
            }
          }}
          onRemove={clearCpaPresetFilters}
          isFixedFilter
        >
          Assigned to me
        </Chip>
      </div>
    );
  }

  return (
    <Filter.Root
      defaultValue=""
      title=""
      trigger={<></>}
      hideDivider
      capsule={
        <>
          <Chip
            onRemove={() => updateFilter(STATUS_UNPAID, null)}
            isActive={Boolean(values.UNPAID)}
            filterType={STATUS_UNPAID}
            onClick={() => updateFilter(STATUS_UNPAID, true)}
            isFixedFilter
          >
            Unpaid
          </Chip>
          <Chip
            onRemove={() => updateFilter(FILTER_INFORMATION_REQUIRED, null)}
            isActive={Boolean(values.INFO_NEEDED_CLIENT)}
            filterType={FILTER_INFORMATION_REQUIRED}
            onClick={() => updateFilter(FILTER_INFORMATION_REQUIRED, true)}
            isFixedFilter
          >
            Information Required
          </Chip>
          <Chip
            onRemove={() => updateFilter(FILTER_BOOKS_PENDING, null)}
            isActive={Boolean(values.BOOKS_PENDING)}
            filterType={FILTER_BOOKS_PENDING}
            onClick={() => updateFilter(FILTER_BOOKS_PENDING, true)}
            isFixedFilter
          >
            Books Pending
          </Chip>
          <Chip
            onRemove={() => updateFilter(FILTER_READY_TO_PICK, null)}
            isActive={Boolean(values.READY_TO_BE_PICKED_UP)}
            filterType={FILTER_READY_TO_PICK}
            onClick={() => updateFilter(FILTER_READY_TO_PICK, true)}
            isFixedFilter
          >
            Ready to Pick
          </Chip>
          <Chip
            onRemove={() => updateFilter(STATUS_DRAFT_SHARED, null)}
            isActive={Boolean(values.DRAFT_SHARED)}
            filterType={STATUS_DRAFT_SHARED}
            onClick={() => updateFilter(STATUS_DRAFT_SHARED, true)}
            isFixedFilter
          >
            Draft Shared
          </Chip>
          <Chip
            onRemove={() => updateFilter(STATUS_READY_TO_FILE, null)}
            isActive={Boolean(values.READY_TO_FILE)}
            filterType={STATUS_READY_TO_FILE}
            onClick={() => updateFilter(STATUS_READY_TO_FILE, true)}
            isFixedFilter
          >
            Ready to File
          </Chip>
          <Chip
            onRemove={() => updateFilter(FILTER_IN_PROGRESS, null)}
            isActive={Boolean(values.PREP_IN_PROGRESS)}
            filterType={FILTER_IN_PROGRESS}
            onClick={() => updateFilter(FILTER_IN_PROGRESS, true)}
            isFixedFilter
          >
            In Progress
          </Chip>
          <Chip
            onRemove={() =>
              updateFilter(FILTER_MANDATORY_INFORMATION_MISSING, null)
            }
            isActive={Boolean(values.MANDATORY_INFO_MISSING)}
            filterType={FILTER_MANDATORY_INFORMATION_MISSING}
            onClick={() =>
              updateFilter(FILTER_MANDATORY_INFORMATION_MISSING, true)
            }
            isFixedFilter
          >
            Mandatory Information Missing
          </Chip>
          <Chip
            onRemove={() => updateFilter(FILTER_INTERNAL_REVIEW, null)}
            isActive={Boolean(values.INTERNAL_REVIEW)}
            filterType={FILTER_INTERNAL_REVIEW}
            onClick={() => updateFilter(FILTER_INTERNAL_REVIEW, true)}
            isFixedFilter
          >
            Internal Review
          </Chip>
          <Chip
            onRemove={() => updateFilter(FILTER_GLOBAL_STATUS_COMPLETED, null)}
            isActive={Boolean(values.FILED)}
            filterType={FILTER_GLOBAL_STATUS_COMPLETED}
            onClick={() => updateFilter(FILTER_GLOBAL_STATUS_COMPLETED, true)}
            isFixedFilter
          >
            Completed
          </Chip>
          <Chip
            onRemove={() => updateFilter(STATUS_ON_HOLD, null)}
            isActive={Boolean(values.ON_HOLD)}
            filterType={STATUS_ON_HOLD}
            onClick={() => updateFilter(STATUS_ON_HOLD, true)}
            isFixedFilter
          >
            On Hold
          </Chip>
          <Chip
            onRemove={() => updateFilter(FILTER_IS_AMENDMENT, null)}
            isActive={Boolean(values.IS_AMENDMENT)}
            filterType={FILTER_IS_AMENDMENT}
            onClick={() => updateFilter(FILTER_IS_AMENDMENT, true)}
            isFixedFilter
          >
            Amendment
          </Chip>
          <Chip
            onRemove={() => updateFilter(FILTER_ETD_UNSET, null)}
            isActive={Boolean(values.UNSET)}
            filterType={FILTER_ETD_UNSET}
            onClick={() => updateFilter(FILTER_ETD_UNSET, true)}
            isFixedFilter
          >
            No ETD
          </Chip>
          <Chip
            onRemove={() => updateFilter(FILTER_ETD_OVERDUE, null)}
            isActive={Boolean(values.OVERDUE)}
            filterType={FILTER_ETD_OVERDUE}
            onClick={() => updateFilter(FILTER_ETD_OVERDUE, true)}
            isFixedFilter
          >
            Overdue
          </Chip>
          <Chip
            onRemove={() => updateFilter(FILTER_ETD_DUE_TODAY, null)}
            isActive={Boolean(values.DUE_TODAY)}
            filterType={FILTER_ETD_DUE_TODAY}
            onClick={() => updateFilter(FILTER_ETD_DUE_TODAY, true)}
            isFixedFilter
          >
            Due today
          </Chip>
        </>
      }
    >
      <></>
    </Filter.Root>
  );
};
